<template>
    <SimpleModal :show="show" class="modal-pausas-root" @close="onClose">
        <div class="modal-pausas-trabalho">
            <h3 class="modal-pausas-titulo">Pausas e retomadas</h3>
            <p v-if="trabalhoId != null" class="modal-pausas-sub fonte-fraca">Trabalho #{{ trabalhoId }}</p>

            <div v-if="loading" class="modal-pausas-loading">Carregando…</div>
            <div v-else-if="!rows.length" class="modal-pausas-vazio fonte-fraca">Nenhum registro de pausa ou retomada para este trabalho.</div>

            <div v-else class="modal-pausas-scroll">
                <table class="tabela modal-pausas-tabela">
                    <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Data e hora</th>
                            <th>Motivo</th>
                            <th>Observação</th>
                            <th>Operador</th>
                            <th class="modal-pausas-col-acao"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="row.id">
                            <td>
                                <span :class="row.pausa === 1 ? 'modal-pausas-tag modal-pausas-tag--pausa' : 'modal-pausas-tag modal-pausas-tag--retoma'">
                                    {{ row.pausa === 1 ? "Pausa" : "Retomada" }}
                                </span>
                            </td>
                            <td>
                                <input
                                    v-model="row.draftDatetimeLocal"
                                    type="datetime-local"
                                    class="modal-pausas-input-dt"
                                />
                            </td>
                            <td>
                                <select
                                    v-if="row.pausa === 1"
                                    class="modal-pausas-select"
                                    :value="row.draftMotivoId == null ? '' : String(row.draftMotivoId)"
                                    @change="onMotivoChange(row, $event)"
                                >
                                    <option value="">—</option>
                                    <option v-for="m in motivos" :key="m.id" :value="String(m.id)">{{ m.nome }}</option>
                                </select>
                                <span v-else class="fonte-fraca">—</span>
                            </td>
                            <td>
                                <textarea v-model="row.draftObs" class="modal-pausas-textarea" rows="2" placeholder="Opcional"></textarea>
                            </td>
                            <td class="modal-pausas-operador">{{ row.colaborador_nome }}</td>
                            <td class="modal-pausas-col-acao">
                                <button
                                    type="button"
                                    class="modal-pausas-btn-salvar"
                                    :disabled="!isRowDirty(row) || savingId === row.id"
                                    :title="savingId === row.id ? 'Salvando…' : 'Salvar alterações deste registro'"
                                    @click="salvarRow(row)"
                                >
                                    {{ savingId === row.id ? "…" : "Salvar" }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="modal-pausas-footer">
                <button type="button" class="acao-secundaria" @click="onClose">Fechar</button>
            </div>
        </div>
    </SimpleModal>
</template>

<script>
import { SimpleModal } from "roboflex-thalamus-componentes-vue3-lib";
import { EditarApontamentoDatasource } from "@/data/datasource/ApontamentoDatasource";
import {
    formatLocalDateTimeForApi,
    sqlDatetimeToDatetimeLocalValue,
} from "@/data/utils/trabalhoCorrecaoPatch";

const ds = new EditarApontamentoDatasource();

export default {
    name: "ModalPausasTrabalho",
    components: { SimpleModal },
    props: {
        show: { type: Boolean, default: false },
        trabalhoId: { type: [Number, String], default: null },
    },
    emits: ["close", "error", "saved"],
    data() {
        return {
            loading: false,
            rows: [],
            motivos: [],
            savingId: null,
        };
    },
    watch: {
        show(val) {
            if (val && this.trabalhoId != null && this.trabalhoId !== "") {
                this.carregar();
            }
            if (!val) {
                this.rows = [];
                this.motivos = [];
            }
        },
        trabalhoId(val) {
            if (this.show && val != null && val !== "") {
                this.carregar();
            }
        },
    },
    methods: {
        onClose() {
            this.$emit("close");
        },

        onMotivoChange(row, event) {
            const v = event.target.value;
            row.draftMotivoId = v === "" ? null : Number(v);
        },

        rowSnapshot(row) {
            return JSON.stringify({
                m: row.draftMotivoId ?? null,
                o: (row.draftObs ?? "").trim(),
                d: row.draftDatetimeLocal ?? "",
            });
        },

        mapTempoToRow(t) {
            const draftMotivoId = t.motivo_id ?? null;
            const draftObs = t.obs ?? "";
            const draftDatetimeLocal = sqlDatetimeToDatetimeLocalValue(t.created_at);
            const row = {
                ...t,
                draftMotivoId,
                draftObs,
                draftDatetimeLocal,
            };
            row._baselineSnap = this.rowSnapshot(row);
            return row;
        },

        isRowDirty(row) {
            return row._baselineSnap !== this.rowSnapshot(row);
        },

        buildPatch(row) {
            const b = JSON.parse(row._baselineSnap);
            const cur = JSON.parse(this.rowSnapshot(row));
            const patch = {};

            if (row.pausa === 1 && cur.m !== b.m) {
                patch.motivo_id = row.draftMotivoId;
            }

            if (cur.o !== b.o) {
                patch.obs = row.draftObs ?? "";
            }

            if (cur.d !== b.d && row.draftDatetimeLocal) {
                const d = new Date(row.draftDatetimeLocal);
                if (!Number.isNaN(d.getTime())) {
                    patch.data_evento = formatLocalDateTimeForApi(d);
                }
            }

            return patch;
        },

        async carregar() {
            const tid = this.trabalhoId;
            if (tid == null || tid === "") return;
            this.loading = true;
            try {
                const [tempos, motivosLista] = await Promise.all([
                    ds.listarTemposTrabalho(tid),
                    ds.listarMotivosInjecao(),
                ]);
                this.motivos = motivosLista;
                this.rows = tempos.map((t) => this.mapTempoToRow(t));
            } catch (e) {
                this.rows = [];
                const msg =
                    e?.response?.data?.error ??
                    e?.response?.data?.msg ??
                    e?.message ??
                    "Não foi possível carregar as pausas.";
                this.$emit("error", typeof msg === "string" ? msg : "Erro ao carregar pausas.");
            } finally {
                this.loading = false;
            }
        },

        async salvarRow(row) {
            const patch = this.buildPatch(row);
            if (Object.keys(patch).length === 0) return;
            this.savingId = row.id;
            try {
                const updated = await ds.atualizarTempoTrabalho(row.id, patch);
                if (!updated) {
                    this.$emit("error", "Resposta inválida ao salvar.");
                    return;
                }
                Object.assign(row, {
                    ...updated,
                    draftMotivoId: updated.motivo_id ?? null,
                    draftObs: updated.obs ?? "",
                    draftDatetimeLocal: sqlDatetimeToDatetimeLocalValue(updated.created_at),
                });
                row._baselineSnap = this.rowSnapshot(row);
                this.$emit("saved");
            } catch (e) {
                this.$emit("error", e?.message ?? "Erro ao salvar.");
            } finally {
                this.savingId = null;
            }
        },
    },
};
</script>

<style scoped>
/* Conteúdo dentro de SimpleModal > .modal-container; largura limitada por .modal-wrapper na lib. */
.modal-pausas-trabalho {
    padding: 4px 0 0;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    text-align: left;
}

.modal-pausas-titulo {
    margin: 0 0 4px;
    text-align: center;
}

.modal-pausas-sub {
    margin: 0 0 16px;
    text-align: center;
}

.modal-pausas-loading,
.modal-pausas-vazio {
    padding: 24px;
    text-align: center;
}

.modal-pausas-scroll {
    overflow-x: hidden;
    overflow-y: auto;
    max-height: min(58vh, 480px);
    width: 100%;
}

.modal-pausas-tabela {
    width: 100%;
    max-width: 100%;
    table-layout: fixed;
    font-size: 0.9rem;
    border-collapse: collapse;
}

.modal-pausas-tabela th,
.modal-pausas-tabela td {
    vertical-align: middle;
    padding: 8px 6px;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.modal-pausas-tabela th:nth-child(1),
.modal-pausas-tabela td:nth-child(1) {
    width: 6.5rem;
}

.modal-pausas-tabela th:nth-child(2),
.modal-pausas-tabela td:nth-child(2) {
    width: 12.5rem;
}

.modal-pausas-tabela th:nth-child(3),
.modal-pausas-tabela td:nth-child(3) {
    width: 16%;
}

.modal-pausas-tabela th:nth-child(4),
.modal-pausas-tabela td:nth-child(4) {
    width: 28%;
}

.modal-pausas-tabela th:nth-child(5),
.modal-pausas-tabela td:nth-child(5) {
    width: 18%;
}

.modal-pausas-col-acao {
    width: 4.75rem;
    white-space: nowrap;
}

.modal-pausas-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
}

.modal-pausas-tag--pausa {
    background: var(--cor-alerta, #fdfd25);
    color: rgba(0, 0, 0, 0.75);
}

.modal-pausas-tag--retoma {
    background: var(--cor-primaria-media, rgba(59, 130, 246, 0.2));
    color: var(--cor-fonte, inherit);
}

.modal-pausas-input-dt {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 6px 8px;
    border: 1px solid var(--cor-separador, #ccc);
    border-radius: 6px;
    font: inherit;
    box-sizing: border-box;
}

.modal-pausas-select {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 6px 8px;
    border-radius: 6px;
    font: inherit;
    box-sizing: border-box;
}

.modal-pausas-textarea {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 6px 8px;
    border: 1px solid var(--cor-separador, #ccc);
    border-radius: 6px;
    font: inherit;
    resize: vertical;
    box-sizing: border-box;
}

.modal-pausas-operador {
    font-size: 0.85rem;
}

.modal-pausas-btn-salvar {
    padding: 6px 10px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    background: var(--cor-primaria, hsl(216 64% 52%));
    color: #fff;
    font-weight: 600;
    font-size: 0.8rem;
}

.modal-pausas-btn-salvar:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.modal-pausas-footer {
    margin-top: 16px;
    display: flex;
    justify-content: center;
}

/*
 * SimpleModal (roboflex-thalamus-componentes-vue3-lib) não usa .modal-content.
 * O limite é .modal-wrapper { max-width: 50rem } (~800px) — por isso o override anterior não surtia efeito.
 */
.modal-pausas-root :deep(.modal-wrapper) {
    max-width: min(92vw, 1100px) !important;
    width: min(92vw, 1100px) !important;
    max-height: 85vh !important;
    box-sizing: border-box;
}

.modal-pausas-root :deep(.modal-container) {
    box-sizing: border-box;
}
</style>
